import {
    Calculation,
    PaymentFrequency,
    PaymentStructure,
} from '@servicetitan/financing-api-client';
import { PlanCard } from './PlanCard';
import { Flex, Grid, Text } from '@servicetitan/anvil2';
import {
    plansListGrid,
    legalDisclosure,
    plansContainer,
    emptyPlansList,
    emptyPlansListText,
} from './PlansList.module.scss';
import {
    SetupDataStore,
    useProviderConfig,
    SecondLookApplicationSubmittedStore,
} from '@servicetitan/financing-workflow-shared';
import { useLegalDisclosure } from '@servicetitan/financing-utils-shared';
import { ReactNode, useState, useMemo, useRef, useEffect } from 'react';
import { PaymentFrequencyTabs } from './PaymentFrequencyTabs';
import { MidTabs, MidInfo } from './MidTabs';
import { v4 as uuidv4 } from 'uuid';

// Filter plans by frequency - moved outside component for performance
const filterPlansByFrequency = (frequency: PaymentFrequency, plansToFilter: Calculation[]) => {
    return plansToFilter.filter(plan => {
        // If no paymentFrequency field exists, show all plans for the 'weekly' frequency to maintain compatibility
        if (!plan.paymentFrequency) {
            return frequency === PaymentFrequency.Weekly;
        }

        // Match frequency values case-insensitively to handle different casing from API
        return plan.paymentFrequency.toLowerCase() === frequency.toLowerCase();
    });
};

const deduplicatePlans = (plans: Calculation[]): Calculation[] => {
    const seen = new Set<string>();
    return plans.filter(plan => {
        if (!plan.planNumber || seen.has(plan.planNumber)) {
            return false;
        }
        seen.add(plan.planNumber);
        return true;
    });
};

export interface PlansListProps {
    plans: Calculation[];
    instructions?: string | ReactNode;
    onPlanSelected?: (plan: Calculation) => void;
    disableSelection?: boolean;
}

export const PlansList = ({
    plans,
    instructions,
    onPlanSelected,
    disableSelection = false,
}: PlansListProps) => {
    const { setupData } = SetupDataStore.useStore();
    const {
        legalDisclosureCategory,
        customLegalDisclosureText,
        customLegalDisclosureTextForDeferredInterest,
        enablePaymentFrequencyTabs,
        supportsMultipleRateSheets,
    } = useProviderConfig();
    const { isSecondLookApplicationSubmitted } = SecondLookApplicationSubmittedStore.useStore();
    const legalDisclosureResult = useLegalDisclosure({
        providerName: setupData?.provider,
        tenantId: setupData?.tenantId,
        category: legalDisclosureCategory,
    });

    const isTechnician = setupData?.role === 'technician';

    const distinctMids = useMemo<MidInfo[]>(() => {
        const seen = new Set<string>();
        const result: MidInfo[] = [];
        for (const plan of plans) {
            if (plan.merchantId && !seen.has(plan.merchantId)) {
                seen.add(plan.merchantId);
                result.push({
                    merchantId: plan.merchantId,
                    // TODO: Remove any cast when FinPlansApi is updated
                    midName: (plan as any).merchantName ?? null,
                });
            }
        }
        return result;
    }, [plans]);

    const showMidTabs =
        isTechnician &&
        supportsMultipleRateSheets === true &&
        !enablePaymentFrequencyTabs &&
        distinctMids.length > 1;

    const [midSelection, setMidSelection] = useState<string | null>(null);

    useEffect(() => {
        if (!showMidTabs) {
            setMidSelection(null);
        }
    }, [showMidTabs]);

    const validatedMidSelection =
        showMidTabs && distinctMids.length > 0
            ? midSelection != null && distinctMids.some(m => m.merchantId === midSelection)
                ? midSelection
                : distinctMids[0].merchantId
            : null;

    const midFilteredPlans = useMemo(() => {
        if (!showMidTabs || validatedMidSelection === null) {
            return plans;
        }
        return plans.filter(p => p.merchantId === validatedMidSelection);
    }, [plans, showMidTabs, validatedMidSelection]);

    const [currentFrequency, setCurrentFrequency] = useState<PaymentFrequency>(
        PaymentFrequency.Weekly,
    );

    // Ref for the grid container to control scroll position
    const gridRef = useRef<HTMLDivElement>(null);

    // Handle frequency change from payment frequency tabs
    const handleFrequencyChange = (frequency: PaymentFrequency) => {
        setCurrentFrequency(frequency);
    };

    const uniquePlans = useMemo(() => deduplicatePlans(midFilteredPlans), [midFilteredPlans]);

    // Derive displayed plans using useMemo - no separate state needed
    const plansToShow = useMemo(() => {
        if (!enablePaymentFrequencyTabs) {
            return uniquePlans;
        }
        return filterPlansByFrequency(currentFrequency, uniquePlans);
    }, [uniquePlans, currentFrequency, enablePaymentFrequencyTabs]);

    // Generate stable UUID keys for each plan object - only when plans change, not on tab switch
    const planKeys = useMemo(() => {
        const keyMap = new Map<Calculation, string>();
        plans.forEach(plan => {
            keyMap.set(plan, uuidv4());
        });
        return keyMap;
    }, [plans]);

    // Check if any plan has deferred interest
    const hasDeferredInterestPlan = useMemo(() => {
        return plansToShow.some(
            plan =>
                plan.paymentStructure === PaymentStructure.DeferredInterest ||
                plan.paymentStructure === PaymentStructure.DeferredInterestAndPrincipal,
        );
    }, [plansToShow]);

    // Show custom disclosure for deferred interest plans only when:
    // 1. The config has a deferred interest disclosure text
    // 2. There are deferred interest plans in the list
    // 3. The second look application has been submitted (meaning we're showing Turns plans)
    const shouldShowDeferredInterestDisclosure =
        customLegalDisclosureTextForDeferredInterest != null &&
        hasDeferredInterestPlan &&
        isSecondLookApplicationSubmitted;

    // Show general custom disclosure (if not using deferred interest specific one)
    const shouldShowCustomDisclosure =
        customLegalDisclosureText != null && customLegalDisclosureTextForDeferredInterest == null;

    // Handle the legal disclosure text, which may be string or string[]
    // Join arrays with line breaks to preserve formatting
    const formattedLegalDisclosureText = Array.isArray(legalDisclosureResult)
        ? legalDisclosureResult.join('<br />')
        : legalDisclosureResult;

    const showFilteredEmptyState = showMidTabs || enablePaymentFrequencyTabs;

    return (
        <Flex direction="column" gap="4" className={plansContainer}>
            {showMidTabs && validatedMidSelection !== null ? (
                <MidTabs
                    mids={distinctMids}
                    selectedMerchantId={validatedMidSelection}
                    onMidChange={setMidSelection}
                    scrollContainerRef={gridRef}
                />
            ) : null}
            {enablePaymentFrequencyTabs ? (
                <PaymentFrequencyTabs
                    plans={plans}
                    onFrequencyChange={handleFrequencyChange}
                    scrollContainerRef={gridRef}
                />
            ) : null}

            <Text variant="headline" el="h2">
                Select a Plan
            </Text>
            {instructions && (
                <Text variant="body" subdued size="small" style={{ marginBottom: '16px' }}>
                    {instructions}
                </Text>
            )}

            {plansToShow.length > 0 ? (
                <Grid ref={gridRef} className={plansListGrid} role="list">
                    {plansToShow.map(plan => (
                        <PlanCard
                            key={planKeys.get(plan)}
                            plan={plan}
                            onPlanSelected={onPlanSelected}
                            showLowestPayment={plansToShow.length > 1}
                            currentFrequency={
                                enablePaymentFrequencyTabs ? currentFrequency : undefined
                            }
                            disableSelection={disableSelection}
                        />
                    ))}
                </Grid>
            ) : showFilteredEmptyState ? (
                <Flex justifyContent="center" alignItems="center" className={emptyPlansList}>
                    <Text variant="body" className={emptyPlansListText}>
                        No plans available to display.
                    </Text>
                </Flex>
            ) : null}

            {/* Custom legal disclosure text for deferred interest plans */}
            {shouldShowDeferredInterestDisclosure && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: customLegalDisclosureTextForDeferredInterest,
                    }}
                />
            )}

            {/* General custom legal disclosure text */}
            {shouldShowCustomDisclosure && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: customLegalDisclosureText,
                    }}
                />
            )}

            {/* Standard legal disclosure text */}
            {formattedLegalDisclosureText !== '' && (
                <Text
                    variant="body"
                    subdued
                    size="small"
                    className={legalDisclosure}
                    dangerouslySetInnerHTML={{
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        __html: formattedLegalDisclosureText,
                    }}
                />
            )}
        </Flex>
    );
};
